import type { ISectionedFilters } from "types/filters";
import type { IPaginationOptions, ISortOptions } from "types/pagination";
import type { IPaginationData, IPaginationResponse } from "utils/pagination";

import { List, Map } from "immutable";
import { ApprovalAlgorithmModel, type IApprovalAlgorithmSchema } from "models/ApprovalAlgorithmModel";
import { ApprovalFlowModel } from "models/ApprovalFlowModel";
import { apiReq } from "utils/api/apiReq";
import { paginationSortFilterToBody } from "utils/api/urlSearchParams";

export type TSearchApprovalAlgorithmsOptions = Omit<ISortOptions, "sortFields">
  & Required<IPaginationOptions>
  & {
    filters?: ISectionedFilters;
    search?: string;
    sortFields?: TWorkflowSortField[];
  };

export type TSearchApprovalAlgorithmsResponse = IPaginationResponse<ApprovalAlgorithmModel> & {
  approvalFlows: Map<string, ApprovalFlowModel>;
};

export type TWorkflowFilterOptions = {
  durations: (null | string)[];
  idpGroupIds: string[];
  names: string[];
  onCallScheduleIds: string[];
};

export type TWorkflowSortField = "lastAdded" | "name" | "stepsAmount";

type TApprovalAlgorithmApiItem = {
  rules?: null | { approvalFlow?: unknown; approvalFlowId?: null | string }[];
};

type TSearchApprovalAlgorithmsApiResponse = {
  pagination: IPaginationData;
  result: TApprovalAlgorithmApiItem[];
};

export async function getApprovalAlgorithmFilterOptions(): Promise<TWorkflowFilterOptions> {
  const { data } = await apiReq<TWorkflowFilterOptions>("GET", "/v1/approvalAlgorithms/filterOptions");

  return data;
}

export async function getApprovalAlgorithms(withUsage = false): Promise<Map<string, ApprovalAlgorithmModel>> {
  const { data } = await apiReq<unknown[]>("GET", `/v1/approvalAlgorithms?withUsage=${withUsage}`);

  return Map(
    data.map((algo: unknown) => {
      const model = ApprovalAlgorithmModel.fromServerData(algo);

      return [model.id, model];
    }),
  );
}

export async function searchApprovalAlgorithms(
  options: TSearchApprovalAlgorithmsOptions,
  abortSignal?: AbortSignal,
): Promise<TSearchApprovalAlgorithmsResponse> {
  const body = paginationSortFilterToBody(options, false);

  const { data } = await apiReq<TSearchApprovalAlgorithmsApiResponse>("POST", "/v1/approvalAlgorithms/search", body, {
    abortSignal,
    noCache: true,
  });

  return {
    approvalFlows: Map(data.result.flatMap(toApprovalFlowEntries)),
    pagination: data.pagination,
    result: List(data.result.map((item) => ApprovalAlgorithmModel.fromServerData(item))),
  };
}

function toApprovalFlowEntries({ rules }: TApprovalAlgorithmApiItem): [string, ApprovalFlowModel][] {
  return (rules ?? []).flatMap(({ approvalFlow, approvalFlowId }) =>
    approvalFlow && approvalFlowId
      ? [[approvalFlowId, ApprovalFlowModel.fromServerData({ id: approvalFlowId, ...approvalFlow })] as const]
      : [],
  );
}

const toSchema = (approvalAlgorithm: ApprovalAlgorithmModel | IApprovalAlgorithmSchema): IApprovalAlgorithmSchema => {
  if (approvalAlgorithm instanceof ApprovalAlgorithmModel) {
    return approvalAlgorithm.toServerData();
  }
  return approvalAlgorithm;
};

export const createApprovalAlgorithm = async (approvalAlgorithm: ApprovalAlgorithmModel | IApprovalAlgorithmSchema) => {
  const schema = toSchema(approvalAlgorithm);
  const { id: _, ...body } = schema;
  const { data: newApprovalAlgorithm } = await apiReq("POST", "/v1/approvalAlgorithms", body);

  return ApprovalAlgorithmModel.fromServerData(newApprovalAlgorithm);
};

export const updateApprovalAlgorithm = async (approvalAlgorithm: ApprovalAlgorithmModel | IApprovalAlgorithmSchema) => {
  const schema = toSchema(approvalAlgorithm);
  const { id, ...body } = schema;
  const { data: updatedApprovalAlgorithm } = await apiReq("PUT", `/v1/approvalAlgorithms/${id}`, body);

  return ApprovalAlgorithmModel.fromServerData(updatedApprovalAlgorithm);
};

export const deleteApprovalAlgorithm = async (approvalAlgorithmId: string) =>
  apiReq("DELETE", `/v1/approvalAlgorithms/${approvalAlgorithmId}`);
